import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import { useTheme } from "../../ThemeContext";
import { staffLogin, getGoogleAuthUrl } from "../../utils/apiClient";
import { EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import { LOGIN_TYPEWRITER_LINES } from "../../utils/constants/typewriterTexts";
import ForceLoginDialog from "./ForceLoginDialog";
import NoSessionDialog from "./NoSessionDialog";
import TermsAndConditionsPopup from "./TermsAndConditionsPopup";
import TypewriterText from "./TypewriterText";
import ForgotPasswordModal from "./ForgotPasswordModal";
import SamlLoginModal from "./SamlLoginModal";
import "./auth-login.css";

const SignIn = ({
  setIsAuthenticated,
  forceLoginDialogVisible,
  setForceLoginDialogVisible,
  loginValidate,
  setNoSessionDialogVisible,
  noSessionDialogVisible,
}) => {
  const { themeMode } = useTheme();
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [forgotPasswordModalVisible, setForgotPasswordModalVisible] = useState(false);
  const [samlLoginModalVisible, setSamlLoginModalVisible] = useState(false);

  // Handle Enter key press
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === "Enter" && !isLoading) {
        handleLogin();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [isLoading, formData]);

  // Input validation functions
  const validateEmail = (email) => {
    const emailRegex = /^.{1,64}@[^\s@]+(\.[^\s@]+){1,255}$/;
    if (!email.trim()) {
      return "Email is required";
    }
    if (!emailRegex.test(email)) {
      return "Please enter a valid email address";
    }
    return null;
  };

  const validatePassword = (password) => {
    return null;
    if (!password) {
      return "Password is required";
    }
    if (password.length < 8 || password.length > 20) {
      return "Password must be between 8 and 20 characters";
    }
    const passwordRegex = /^(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])[A-Za-z\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+$/;
    if (!passwordRegex.test(password)) {
      return "Password must contain at least one lowercase letter, one number, and one special character";
    }
    return null;
  };

  // Form handlers
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;
    
    const passwordError = validatePassword(formData.password);
    if (passwordError) newErrors.password = passwordError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Authentication handlers
  const handleLogin = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    
    try {
      message.loading("Signing in...", 0);
      
      const response = await staffLogin({
        email: formData.email.trim(),
        password: formData.password,
      });

      message.destroy();

      if (response.statusCode === "SUCCESS") {
        setIsAuthenticated(true);
        const staff = response.response.staff;
        
        // 🏢 Set facility check session variable for admin/supervisor users
        const userRoles = Array.isArray(staff.role) ? staff.role : [staff.role];
        const isAdminOrSupervisor = userRoles.some(role => 
          role && (role.toLowerCase() === 'admin' || role.toLowerCase() === 'supervisor')
        );
        
        if (isAdminOrSupervisor) {
          sessionStorage.setItem('facilityCheck', 'false');
          console.log('✅ Set facilityCheck session variable for admin/supervisor user');
        }
        
        if (staff.changePassword) {
          message.success("Login successful! Please change your password.");
          setTimeout(() => navigate("/change-password"), 1000);
        } else {
          // Check if onboarding is complete
          const onboardingComplete = response.response.onboardingComplete;
          
          if (onboardingComplete === true) {
            message.success("Login successful!");
            setTimeout(() => {
              navigate("/home");
              window.location.reload();
            }, 3000);
          } else {
            // Onboarding not complete, redirect to onboarding flow
            message.success("Login successful! Let's complete your setup.");
            setTimeout(() => {
              navigate("/onboarding/document-signing");
            }, 2000);
          }
        }
      } else {
        message.error(response.message || "Invalid email or password. Please try again.");
      }
    } catch (error) {
      message.destroy();
      message.error(error.response.data.error || "Something went wrong. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setForgotPasswordModalVisible(true);
  };

  // OAuth handlers
  const handleGoogleLogin = async () => {
    try {
      message.loading("Redirecting to Google...", 0);
      
      const response = await getGoogleAuthUrl();
      
      message.destroy();
      
      if (response.statusCode === "SUCCESS" && response.response.auth_url) {
        // Store the state for validation (optional, since backend handles it)
        if (response.response.state) {
          sessionStorage.setItem("oauth_state", response.response.state);
        }
        
        // Redirect to Google OAuth
        window.location.href = response.response.auth_url;
      } else {
        message.error("Failed to get Google authorization URL. Please try again.");
      }
    } catch (error) {
      message.destroy();
      console.error("Google OAuth error:", error);
      message.error("Failed to initiate Google login. Please try again.");
    }
  };

  const handleSamlLogin = () => {
    setSamlLoginModalVisible(true);
  };

  // Dialog handlers
  const handleForceLoginDialogResult = (result) => {
    setForceLoginDialogVisible(false);
    
    if (result === "continue") {
      handleLogin();
    }
  };

  const handleNoSessionsDialogResult = () => {
    setNoSessionDialogVisible(false);
  };

  return (
    <div 
      className={`auth-login-container ${themeMode}`}
      style={{
        backgroundColor: themeMode === 'light' ? '#ffffff' : '#04002b'
      }}
    >
      {/* Left Column - Form */}
      <div className="auth-login-form-section">
        <div className="auth-login-form-content">
          <h1 className="auth-login-title">Sign In</h1>
          
          {/* SSO Buttons */}
          <div className="auth-login-sso-section">
            <button
              className="auth-login-sso-button google-button"
              onClick={handleGoogleLogin}
              disabled={isLoading}
            >
              <img 
                src={`${process.env.PUBLIC_URL}/login/google-icon.svg.svg`} 
                alt="Google" 
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              Sign in with Google
            </button>
            <button
              className="auth-login-sso-button saml-button"
              onClick={handleSamlLogin}
              disabled={isLoading}
            >
              <img 
                src={`${process.env.PUBLIC_URL}/login/saml-icon.svg`} 
                alt="SAML" 
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              Sign in with SAML
            </button>
          </div>

          {/* Divider */}
          <div className="auth-login-divider">
            <span>or</span>
          </div>

          {/* Form Fields */}
          <div className="auth-login-form">
            <div className="auth-login-field">
              <input
                type="email"
                placeholder="Email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                className={`auth-login-input ${errors.email ? "error" : ""}`}
                disabled={isLoading}
              />
              {errors.email && (
                <span className="auth-login-error">{errors.email}</span>
              )}
            </div>

            <div className="auth-login-field">
              <div className="auth-login-password-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={formData.password}
                  onChange={(e) => handleInputChange("password", e.target.value)}
                  className={`auth-login-input ${errors.password ? "error" : ""}`}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="auth-login-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                >
                  {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                </button>
              </div>
              {errors.password && (
                <span className="auth-login-error">{errors.password}</span>
              )}
            </div>

            {/* Forgot Password */}
            <div className="auth-login-forgot-password">
              <button
                type="button"
                className="auth-login-forgot-link"
                onClick={handleForgotPassword}
                disabled={isLoading}
              >
                Forgot Password?
              </button>
            </div>

            {/* Sign In Button */}
            <button
              className="auth-login-submit-button"
              onClick={handleLogin}
              disabled={isLoading}
            >
              {isLoading ? "Signing In..." : "Sign In"}
            </button>
          </div>
        </div>
      </div>

      {/* Right Column - Hero */}
      <div className="auth-login-hero-section">
        <div 
          className="auth-login-hero-background"
          style={{
            position: 'absolute',
            top: '2rem',
            right: '2rem',
            bottom: '2rem',
            left: '2rem',
            backgroundImage: `url(${process.env.PUBLIC_URL}/login/login-background.png)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            borderRadius: '24px',
            zIndex: 1
          }}
        />
        {/* Logo positioned in top right corner */}
        <div className="auth-login-corner-logo">
          <img
            src={`${process.env.PUBLIC_URL}/login/login-logo.png`}
            alt="VoiceCare Logo"
            className="auth-login-corner-logo-img"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
        <div className="auth-login-hero-content">
          <img
            src={`${process.env.PUBLIC_URL}/login/voicecare-logo.svg`}
            alt="VoiceCare AI"
            className="auth-login-hero-logo"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <TypewriterText 
            lines={LOGIN_TYPEWRITER_LINES}
            speed={50}
            deleteSpeed={30}
            delay={1000}
            pauseBetweenCycles={2000}
            className="auth-login-hero-title"
            showCursor={true}
            cycle={true}
          />
        </div>
      </div>

      {/* Dialogs */}
      {forceLoginDialogVisible && (
        <ForceLoginDialog
          onResult={handleForceLoginDialogResult}
          onCancel={setForceLoginDialogVisible}
        />
      )}

      {noSessionDialogVisible && (
        <NoSessionDialog
          onResult={handleNoSessionsDialogResult}
          onCancel={setNoSessionDialogVisible}
        />
      )}

      <TermsAndConditionsPopup />

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        open={forgotPasswordModalVisible}
        onClose={() => setForgotPasswordModalVisible(false)}
      />

      {/* SAML Login Modal */}
      <SamlLoginModal
        open={samlLoginModalVisible}
        onClose={() => setSamlLoginModalVisible(false)}
      />
    </div>
  );
};

export default SignIn; 